import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes, FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

const menuItems = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Experience', path: '/experience' },
  { name: 'Projects', path: '/projects' },
  { name: 'Articles', path: '/articles' },
  { name: 'Contact', path: '/contact' },
];

const sidebarSocials = [
  { href: 'https://www.linkedin.com/in/ayoashy/', icon: FaLinkedin, label: 'LinkedIn', color: '#0078b2' },
  { href: 'https://github.com/ayoashy', icon: FaGithub, label: 'Github', color: '#24292e' },
  { href: 'mailto:ashiruayo@gmail.com', icon: HiOutlineMail, label: 'Email', color: '#ce463e' },
  { href: 'https://twitter.com/topboyshoks', icon: FaTwitter, label: 'Twitter', color: '#1d9bf0' },
];

const mobileSocials = [
  { href: 'https://www.linkedin.com/in/ayoashy/', icon: FaLinkedin },
  { href: 'https://github.com/ayoashy', icon: FaGithub },
  { href: 'mailto:ashiruayo@gmail.com', icon: HiOutlineMail },
  { href: 'https://twitter.com/topboyshoks', icon: FaTwitter },
];

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = () => setNav(false);

  return (
    <>
      {/* ── Main Navbar ─────────────────────────────────── */}
      <motion.nav
        className={`fixed w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-[#1a1a1a] py-4'
            : 'bg-transparent py-6'
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <NavLink to="/" onClick={close}>
            <motion.div
              className="font-bold text-xl font-mono tracking-widest"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400 }}
            >
              <span className="text-[#4d760c]">AY</span>
              <span className="text-white">Ò</span>
              <span className="text-[#4d760c] text-xs ml-0.5">_</span>
            </motion.div>
          </NavLink>

          {/* Desktop Menu */}
          <ul className="hidden md:flex items-center gap-1">
            {menuItems.map((item, i) => (
              <li key={item.name} className="px-0">
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm font-mono transition-colors duration-300 block ${
                      isActive
                        ? 'text-[#6ea60f]'
                        : 'text-slate-500 hover:text-[#6ea60f]'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="text-[#4d760c] text-xs mr-1 opacity-60">
                        0{i + 1}.
                      </span>
                      {item.name}
                      {isActive && (
                        <motion.span
                          className="absolute bottom-0 left-4 right-4 h-px bg-[#4d760c] block"
                          layoutId="nav-underline"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Hamburger */}
          <button
            onClick={() => setNav(!nav)}
            className="md:hidden text-slate-400 hover:text-[#6ea60f] transition-colors z-50 relative p-1"
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait">
              {nav ? (
                <motion.span
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaTimes size={22} />
                </motion.span>
              ) : (
                <motion.span
                  key="bars"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaBars size={22} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </motion.nav>

      {/* ── Mobile Full-Screen Menu ───────────────────────── */}
      <AnimatePresence>
        {nav && (
          <motion.div
            className="fixed inset-0 z-40 bg-[#050505]/97 backdrop-blur-2xl flex flex-col items-center justify-center"
            initial={{ opacity: 0, clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            animate={{ opacity: 1, clipPath: 'circle(150% at calc(100% - 40px) 40px)' }}
            exit={{ opacity: 0, clipPath: 'circle(0% at calc(100% - 40px) 40px)' }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <ul className="flex flex-col items-center gap-6">
              {menuItems.map((item, i) => (
                <motion.li
                  key={item.name}
                  className="px-0"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 24 }}
                  transition={{ delay: i * 0.07, duration: 0.35 }}
                >
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={close}
                    className={({ isActive }) =>
                      `text-4xl font-bold font-mono transition-colors duration-300 ${
                        isActive ? 'text-[#4d760c]' : 'text-slate-300 hover:text-[#6ea60f]'
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                </motion.li>
              ))}
            </ul>

            {/* Social icons in mobile menu */}
            <motion.div
              className="flex gap-7 mt-14"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.4 }}
            >
              {mobileSocials.map(({ href, icon: Icon }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-[#6ea60f] transition-colors duration-300"
                >
                  <Icon size={22} />
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Desktop Social Sidebar ───────────────────────── */}
      <motion.div
        className="hidden lg:flex fixed flex-col top-[35%] left-0 z-40"
        initial={{ x: -160, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 1, duration: 0.6, ease: 'easeOut' }}
      >
        <ul className="flex flex-col">
          {sidebarSocials.map(({ href, icon: Icon, label, color }) => (
            <li
              key={label}
              className="w-[160px] h-[50px] flex justify-between items-center ml-[-110px] hover:ml-[-10px] duration-300 px-4"
              style={{ backgroundColor: color }}
            >
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex justify-between items-center w-full text-white text-xs font-mono"
              >
                {label}
                <Icon size={20} />
              </a>
            </li>
          ))}
        </ul>
      </motion.div>
    </>
  );
};

export default Navbar;
